
import React, { useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onBackToSignIn: () => void;
}

const RecoverKeyModal: React.FC<Props> = ({ isOpen, onClose, onBackToSignIn }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate recovery protocol
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/75 animate-in fade-in duration-200">
      <div 
        className="w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Password Reset</span>
            <h2 className="text-base font-bold tracking-tight font-heading text-white leading-tight mt-0.5">Reset Password</h2>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
          >
            <i className="fas fa-times text-base"></i>
          </button>
        </header>

        <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
          {!success ? (
            <>
              <div className="space-y-2">
                <p className="text-[11px] font-medium text-white/60 leading-relaxed font-dm">
                  Enter your registered email address to receive password reset instructions.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">User Email</label>
                  <div className="relative">
                    <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                    <input 
                      type="email"
                      required
                      placeholder="user@example.com"
                      className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 font-bold uppercase text-[11px] tracking-[0.2em] rounded-lg transition-colors font-heading active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2.5 cursor-pointer"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin text-xs"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane text-xs"></i>
                        Send Reset Link
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center space-y-5 py-4 animate-in fade-in zoom-in-95 duration-300">
              <div className="w-14 h-14 bg-[#F26522]/15 text-[#F26522] rounded-xl flex items-center justify-center mx-auto border border-[#F26522]/25">
                <i className="fas fa-check-double text-xl"></i>
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">Reset Link Sent</h3>
                <p className="text-xs font-medium text-white/60 leading-relaxed px-4 font-dm">
                  A password reset link has been sent to <span className="text-white font-bold">{email}</span>. Please check your inbox to reset your password.
                </p>
              </div>
              <button 
                onClick={onBackToSignIn}
                className="w-full py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[11px] font-bold uppercase tracking-[0.2em] rounded-lg transition-colors font-heading cursor-pointer"
              >
                Return to Sign In
              </button>
            </div>
          )}

          {!success && (
            <button 
              type="button"
              onClick={onBackToSignIn}
              className="w-full py-3 border border-white/10 text-[10px] font-bold uppercase tracking-[0.15em] text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
            >
              Back to Sign In
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoverKeyModal;
